import { UploadManager } from "@context-providers/file-upload/upload-manager";
import { UploadManagerWorker } from "@context-providers/file-upload/upload-manager-worker";
import { GetUploadFn, RemoveTaskFn, StartUploadFn, UpdateTaskFn, UploadManagerInterface } from "@custom-types/upload-manager-types";

let isLogEnabled = true;

/** Allow to disable logging so the "npm test" output is not cluttered. */
export function enableCreateUploadManagerLog(isEnabled: boolean): void {
  isLogEnabled = isEnabled;
}

/**
 * Factory function to create an instance of UploadManager
 * @param getTaskFromStore Function to get a task from the store by its id
 * @param startTaskInStore Function to start a background task in the store
 * @param updateTaskInStore Function to update the task in the store corresponding to the upload
 * @param removeTaskFromStore Function to remove a task from the store.
 * @returns Instance of an UploadManager
 */
export function createUploadManager(
  getTaskFromStore: GetUploadFn,
  startTaskInStore: StartUploadFn,
  updateTaskInStore: UpdateTaskFn,
  removeTaskFromStore: RemoveTaskFn
): UploadManagerInterface {
  try {
    // The developer experience of SharedWorkers is not great in browsers yet,
    // especially too many interactions are required to access a SharedWorker's DevTools.
    // This can be annoying if devs e.g. want to fake a slow/broken internet connection.
    // Using the old UploadManager, everything is reachable from the main window's DevTools.
    // ?uploadManagerWorker=false - Previous parameter.
    // ?uploadWorker=false        - Added for lazy typing.
    if (!globalThis.location.search.match(/[?&]upload(Manager)?Worker=false\b/i)) {
      return new UploadManagerWorker(
        getTaskFromStore,
        startTaskInStore,
        updateTaskInStore,
        removeTaskFromStore
      );
    }
  } catch (error) {
    // Fallback to the UploadManager if SharedWorker is not supported.
    // If the user's browser is expected to support SharedWorker, we could log to Sentry additionally.
    if (isLogEnabled) {
      // eslint-disable-next-line no-console -- Should be visible for devs at least.
      console.error("UploadManagerWorker could not be initialized, using fallback.", error);
    }
  }
  return new UploadManager(
    getTaskFromStore,
    startTaskInStore,
    updateTaskInStore,
    removeTaskFromStore
  );
}

/**
 * Checks for the provided worker upload manager that it was able to connect to its worker.
 * If not, it returns a standard upload manager as fallback.
 * @param getTaskFromStore Function to get a task from the store by its id
 * @param startTaskInStore Function to start a background task in the store
 * @param updateTaskInStore Function to update the task in the store corresponding to the upload
 * @param removeTaskFromStore Function to remove a task from the store.
 * @param uploadManager Worker upload manager.
 * @returns Instance of an UploadManager as fallback if the connection could not be established.
 *          undefined if the connection was successful.
 */
export async function waitForConnection(
  getTaskFromStore: GetUploadFn,
  startTaskInStore: StartUploadFn,
  updateTaskInStore: UpdateTaskFn,
  removeTaskFromStore: RemoveTaskFn,
  uploadManager: UploadManagerWorker
): Promise<UploadManager | undefined> {
    const isConnected = await uploadManager.connectionPromise();
    if (isConnected) {
      return undefined;
    } else {
      if (isLogEnabled) {
        // eslint-disable-next-line no-console -- Should be visible for devs at least.
        console.error("UploadManagerWorker could not connect to SharedWorker, using fallback.");
      }
      return new UploadManager(
        getTaskFromStore,
        startTaskInStore,
        updateTaskInStore,
        removeTaskFromStore
      );
    }
}
